import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress, Modal } from "@mui/material";

import { questionSchema } from "./validations";
import { useAppDispatch } from "application/store";
import { SupportedLanguages } from "integration/api/models";
import { openSnackBar } from "application/store/global/slice";
import { QuestionFormData, QuestionForQAModalProps } from "./models";
import { supportedLanguagesArray } from "application/constants/array";
import { registerWebinarQuestion } from "application/store/Deal/actions";
import { RegisterWebinarQuestionDto } from "integration/api/Deal/models";

import TextArea from "application/components/TextArea";
import { Button, OutlineButton } from "application/components";
import ReplyLanguage from "features/question-answer/components/ReplyLanguage";

import {
  Card,
  Content,
  LogoIcon,
  Container,
  LanguagesList,
  ButtonsSection,
  LanguagesLabel,
  LoaderContainer,
} from "./styles";

const QuestionForQAModal = ({ deal, visible, loading, onClose }: QuestionForQAModalProps) => {
  const dispatch = useAppDispatch();

  const {
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<QuestionFormData>({
    mode: "onChange",
    defaultValues: {
      replyLanguage: SupportedLanguages.EN,
    },
    resolver: yupResolver(questionSchema),
  });

  const setReplyLanguage = (value: SupportedLanguages) => {
    setValue("replyLanguage", value);
  };

  const triggerErrorSnackBar = () => {
    dispatch(openSnackBar({ severity: "error", message: "Something went wrong!" }));
  };

  const triggerSuccessSnackBar = (noQuestion: boolean) => {
    const message = noQuestion
      ? "Registration confirmed."
      : "We’ll display the answer as soon as it’s ready.";
    dispatch(openSnackBar({ message, severity: "success" }));
  };

  const request = async (args: RegisterWebinarQuestionDto) => {
    try {
      await dispatch(registerWebinarQuestion(args)).unwrap();
      onClose();
      triggerSuccessSnackBar(args.noQuestion);
    } catch (error) {
      onClose();
      triggerErrorSnackBar();
    }
  };

  const onSubmit = ({ question, replyLanguage }: QuestionFormData) => {
    if (!deal?.id) {
      return;
    }
    const args: RegisterWebinarQuestionDto = {
      question,
      id: deal.id,
      replyLanguage,
      noQuestion: false,
    };
    request(args);
  };

  const onReject = () => {
    if (!deal?.id) {
      return;
    }
    const args: RegisterWebinarQuestionDto = {
      id: deal.id,
      noQuestion: true,
    };
    request(args);
  };

  return (
    <Modal open={visible}>
      <Container>
        <Card>
          <LogoIcon />
          <Content>
            <h1>You’re registered for Q&A!</h1>
            <p>
              Have a question in mind?
              <br />
              Share it now and it might be answered ahead of or during the session.
            </p>
          </Content>

          <TextArea
            id="question"
            {...register("question")}
            filled={!!watch("question")}
            label="Write your question here…"
            errorMessage={errors.question?.message}
          />

          <LanguagesLabel>Reply language:</LanguagesLabel>
          <LanguagesList>
            {supportedLanguagesArray.map((lng) => (
              <ReplyLanguage
                key={lng}
                lng={lng}
                setLanguage={setReplyLanguage}
                checked={lng === watch("replyLanguage")}
              />
            ))}
          </LanguagesList>

          <ButtonsSection>
            {loading ? (
              <LoaderContainer>
                <CircularProgress size="24px" />
              </LoaderContainer>
            ) : (
              <React.Fragment>
                <OutlineButton onClick={onReject}>No questions</OutlineButton>
                <Button type="submit" disabled={!isValid} onClick={handleSubmit(onSubmit)}>
                  Submit
                </Button>
              </React.Fragment>
            )}
          </ButtonsSection>
        </Card>
      </Container>
    </Modal>
  );
};

export default QuestionForQAModal;
